import { isDevelopmentNodeEnvironment } from "@src/appV2/environment";
import { isCapacitorPlatform } from "@src/appV2/lib";
import { useLocalStorage } from "@src/appV2/lib/utils";
import { useCallback, useEffect } from "react";

interface MobileConsoleConfig {
  enabled: boolean;
  maxLogs: number;
  position: "top" | "bottom";
}

const DEFAULT_CONFIG: MobileConsoleConfig = {
  enabled: true,
  maxLogs: 100,
  position: "bottom",
};

/**
 * Hook to manage mobile console configuration and state
 */
export function useMobileConsole(config: Partial<MobileConsoleConfig> = {}) {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };

  const [userVisibility, setUserVisibility] = useLocalStorage(
    "mobile-console-visible",
    finalConfig.enabled
  );

  // Compute final visibility based on environment constraints and user preference
  const isVisible = isDevelopmentNodeEnvironment() && isCapacitorPlatform() && userVisibility;

  const toggleVisibility = useCallback(() => {
    setUserVisibility((previous) => !previous);
  }, [setUserVisibility]);

  const show = useCallback(() => {
    setUserVisibility(true);
  }, [setUserVisibility]);

  const hide = useCallback(() => {
    setUserVisibility(false);
  }, [setUserVisibility]);

  // Register global controls
  useEffect(() => {
    if (typeof globalThis !== "undefined" && isDevelopmentNodeEnvironment()) {
      (globalThis as any).cbhMobileConsole?.registerControls?.(toggleVisibility, show, hide);
    }
  }, [toggleVisibility, show, hide]);

  return {
    isVisible,
    config: finalConfig,
    toggleVisibility,
    show,
    hide,
  };
}

/**
 * Global mobile console controls for browser console access
 * Usage in browser console:
 * - window.cbhMobileConsole.show()
 * - window.cbhMobileConsole.hide()
 * - window.cbhMobileConsole.toggle()
 */
if (typeof globalThis !== "undefined" && isDevelopmentNodeEnvironment()) {
  let globalToggleFunction: (() => void) | undefined;
  let globalShowFunction: (() => void) | undefined;
  let globalHideFunction: (() => void) | undefined;

  (globalThis as any).cbhMobileConsole = {
    show: () => globalShowFunction?.(),
    hide: () => globalHideFunction?.(),
    toggle: () => globalToggleFunction?.(),
    registerControls: (toggle: () => void, show: () => void, hide: () => void) => {
      globalToggleFunction = toggle;
      globalShowFunction = show;
      globalHideFunction = hide;
    },
  };
}
